/**
 * Same-origin helpers for top-level Entra handoffs from the CDF preview iframe
 * (or any other embedded panel). Interactive Entra pages cannot render in a
 * generic iframe — open a top-level tab instead.
 */

import type { AccountInfo } from '@azure/msal-browser';

const LOGOUT_TARGET_PARAM = 'logoutTarget';

/** Credential-free identity snapshot, used only to target account-scoped logout. */
export function logoutTarget(account: AccountInfo): AccountInfo {
  return { homeAccountId: account.homeAccountId, localAccountId: account.localAccountId,
    tenantId: account.tenantId, environment: account.environment, username: '' };
}
export function readLogoutTarget(raw: string | null): AccountInfo | undefined {
  if (!raw || raw.length > 2048) return undefined;
  try {
    const value: unknown = JSON.parse(raw);
    if (!value || typeof value !== 'object') return undefined;
    const target = value as Record<string, unknown>;
    for (const key of ['homeAccountId', 'localAccountId', 'tenantId', 'environment']) {
      if (typeof target[key] !== 'string' || !/^[a-z0-9._-]{1,512}$/i.test(target[key])) return undefined;
    }
    return logoutTarget(target as unknown as AccountInfo);
  } catch { return undefined; }
}

export const AUTH_ACTION_PARAM = 'authAction';
export const RETURN_TO_PARAM = 'returnTo';
const SIGN_IN_HANDOFF_PARAM = 'signInHandoff';
export interface SignInHandoff { id: string; expiresAt: number }
function readSignInHandoff(raw: string | null): SignInHandoff | undefined {
  if (!raw || raw.length > 200) return undefined;
  try {
    const value: unknown = JSON.parse(raw);
    if (!value || typeof value !== 'object' || !('id' in value) ||
      typeof value.id !== 'string' || !/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(value.id) ||
      !('expiresAt' in value) || typeof value.expiresAt !== 'number' ||
      !Number.isSafeInteger(value.expiresAt) || value.expiresAt <= Date.now() ||
      value.expiresAt > Date.now() + 10 * 60_000) return undefined;
    return { id: value.id, expiresAt: value.expiresAt };
  } catch { return undefined; }
}

/** Graph consent actions request only the permission needed by the user action. */
export type AuthAction = 'login' | 'logout' | 'acquireToken' | 'acquireGraphCalendar' | 'acquireGraphPlaces' | 'acquireGraphPresence' | 'acquireGraphDirectory' | 'acquireGraphManagerReports' | 'acquireGraphGroups';

const AUTH_ACTIONS = new Set<AuthAction>([
  'login',
  'logout',
  'acquireToken',
  'acquireGraphCalendar',
  'acquireGraphPlaces',
  'acquireGraphPresence',
  'acquireGraphDirectory',
  'acquireGraphManagerReports',
  'acquireGraphGroups'
]);

export function isInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

/** Accept only relative same-origin app paths. Reject protocol-relative / absolute URLs. */
export function isSafeReturnTo(path: string): boolean {
  if (!path.startsWith('/')) return false;
  if (path.startsWith('//')) return false;
  if (path.includes('://')) return false;
  if (path.includes('\\')) return false;
  return true;
}

function currentReturnTo(): string {
  const combined = `${window.location.pathname}${window.location.search}`;
  return isSafeReturnTo(combined) ? combined : '/';
}

/** Build a top-level URL that carries a single-use auth intent. */
export function buildTopLevelAuthUrl(action: AuthAction, returnTo?: string, account?: AccountInfo, signInHandoff?: SignInHandoff): string {
  const url = new URL(window.location.origin + '/');
  url.searchParams.set(AUTH_ACTION_PARAM, action);
  if (action === 'login' && signInHandoff) url.searchParams.set(SIGN_IN_HANDOFF_PARAM, JSON.stringify(signInHandoff));
  if (action === 'logout' && account) url.searchParams.set(LOGOUT_TARGET_PARAM, JSON.stringify(logoutTarget(account)));

  const candidate = returnTo ?? currentReturnTo();
  if (isSafeReturnTo(candidate)) {
    url.searchParams.set(RETURN_TO_PARAM, candidate);
  }

  url.hash = '';
  return url.toString();
}

/**
 * Read and remove a single-use auth intent from the URL before any interaction
 * starts. Idempotent for subsequent Strict Mode remounts (param is already gone).
 */
export function consumeAuthIntent(): { action: AuthAction; returnTo: string | null; account?: AccountInfo; signInHandoff?: SignInHandoff } | null {
  const url = new URL(window.location.href);
  const raw = url.searchParams.get(AUTH_ACTION_PARAM);
  if (!raw || !AUTH_ACTIONS.has(raw as AuthAction)) return null;

  const action = raw as AuthAction;
  const returnRaw = url.searchParams.get(RETURN_TO_PARAM);
  const returnTo = returnRaw && isSafeReturnTo(returnRaw) ? returnRaw : null;

  const account = action === 'logout' ? readLogoutTarget(url.searchParams.get(LOGOUT_TARGET_PARAM)) : undefined;
  const signInHandoff = action === 'login' ? readSignInHandoff(url.searchParams.get(SIGN_IN_HANDOFF_PARAM)) : undefined;
  url.searchParams.delete(SIGN_IN_HANDOFF_PARAM);
  url.searchParams.delete(LOGOUT_TARGET_PARAM);
  url.searchParams.delete(AUTH_ACTION_PARAM);
  url.searchParams.delete(RETURN_TO_PARAM);
  const next = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState({}, document.title, next);

  return { action, returnTo, ...(account ? { account } : {}), ...(signInHandoff ? { signInHandoff } : {}) };
}

/**
 * Opens a top-level tab for Entra interaction. Must remain synchronous to the
 * user click stack so popup policies allow it.
 */
export function openTopLevelAuth(action: AuthAction, returnTo?: string, account?: AccountInfo, signInHandoff?: SignInHandoff): boolean {
  const opened = window.open(buildTopLevelAuthUrl(action, returnTo, account, signInHandoff), '_blank', 'noopener,noreferrer');
  if (!opened) {
    // noopener may return null even when navigation succeeded. The caller
    // must keep external logout unconfirmed and offer a retry either way.
    return false;
  }
  return true;
}
